import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { ApiError, type SyncEntityProgress, type SyncStatusResponse } from "../api/types";
import { useAuth } from "../store/auth";
import { useLocale } from "../hooks/useLocale";
import styles from "./OnboardingPage.module.css";

const POLL_INTERVAL_MS = 5_000;
const OAUTH_TIMEOUT_MS = 10 * 60_000;

// Order mirrors the orchestrator's import sequence so the list reads top-to-bottom
// as work progresses. Keys must match the entries written by
// internal/etl/orchestrator.go's updateEntityProgress calls.
const SYNC_ENTITIES: ReadonlyArray<{ key: string; labelKey: string }> = [
  { key: "leads", labelKey: "table.leads" },
  { key: "contacts", labelKey: "table.contacts" },
  { key: "managers", labelKey: "table.managers" },
  { key: "pipelines", labelKey: "table.pipelines" },
  { key: "pipeline_events", labelKey: "table.pipeline_events" },
  { key: "calls", labelKey: "table.calls" },
  { key: "messages", labelKey: "table.messages" },
];

function entityStateClass(p: SyncEntityProgress | undefined): string {
  if (p?.status === "done") return styles.entityDone;
  if (p?.status === "loading" || p?.status === "running") return styles.entityLoading;
  return styles.entityWaiting;
}

// Onboarding has two stages, gated on tenant.amocrm_domain:
//
//  1. amoCRM not yet attached → OAuth consent popup.
//  2. amoCRM attached → existing sync-progress polling UI.
//
// Stage transition is automatic: connecting amoCRM kicks off the initial
// import on the backend; we then loadTenant() to refresh `tenant.amocrm_*`,
// which flips the gate.
export function OnboardingPage() {
  const tenant = useAuth((s) => s.tenant);
  if (!tenant?.amocrm_domain) {
    return <ConnectAmoCRMForm />;
  }
  return <SyncProgress />;
}

interface AmoCRMOAuthMessage {
  type: "amocrm-oauth";
  status: "ok" | "error";
  error?: string;
}

function ConnectAmoCRMForm() {
  const { t, tOr } = useLocale();
  const beginAmoCRMOAuth = useAuth((s) => s.beginAmoCRMOAuth);
  const loadTenant = useAuth((s) => s.loadTenant);
  const canConnect = useAuth((s) => s.currentUser?.role === "owner");
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [errCode, setErrCode] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const cancelWaitRef = useRef<(() => void) | null>(null);

  useEffect(() => () => {
    cancelWaitRef.current?.();
    popupRef.current?.close();
  }, []);

  // Onboarding is post-signup but pre-amoCRM-connection — the user may
  // realize they want to abort and start over with a different account.
  // Logout is the only escape hatch from this page.

  async function onConnect() {
    if (busy) return;
    setErrCode(null);

    // Open synchronously from the click so popup protection does not mistake
    // this for an unsolicited window while the state API request is in flight.
    const popup = window.open(
      "about:blank",
      "amocrm-oauth",
      "popup=yes,scrollbars=yes,resizable=yes,width=750,height=580",
    );
    if (!popup) {
      setErrCode("popup_blocked");
      return;
    }
    popupRef.current = popup;
    setBusy(true);

    try {
      const authorizationURL = await beginAmoCRMOAuth();

      await new Promise<void>((resolve, reject) => {
        let settled = false;
        const cleanup = () => {
          window.removeEventListener("message", onMessage);
          window.clearTimeout(timeout);
          window.clearInterval(closedPoll);
          cancelWaitRef.current = null;
        };
        const finish = (fn: () => void) => {
          if (settled) return;
          settled = true;
          cleanup();
          fn();
        };
        const onMessage = (event: MessageEvent<unknown>) => {
          if (event.origin !== window.location.origin || event.source !== popup) return;
          const message = event.data as Partial<AmoCRMOAuthMessage> | null;
          if (!message || message.type !== "amocrm-oauth") return;
          if (message.status === "ok") {
            finish(resolve);
          } else {
            finish(() => reject(new Error(message.error || "oauth_failed")));
          }
        };
        const timeout = window.setTimeout(
          () => finish(() => reject(new Error("oauth_timeout"))),
          OAUTH_TIMEOUT_MS,
        );
        const closedPoll = window.setInterval(() => {
          if (popup.closed) finish(() => reject(new Error("popup_closed")));
        }, 500);
        cancelWaitRef.current = () => finish(() => reject(new Error("popup_closed")));
        window.addEventListener("message", onMessage);
        popup.location.replace(authorizationURL);
      });

      // Refresh tenant so the gate flips to SyncProgress on next render.
      await loadTenant();
      navigate("/onboarding", { replace: true });
    } catch (err) {
      popup.close();
      if (err instanceof ApiError) setErrCode(err.code);
      else if (err instanceof Error) setErrCode(err.message);
      else setErrCode("unknown");
      setBusy(false);
    } finally {
      popupRef.current = null;
    }
  }

  return (
    <main className={styles.shell}>
      <LogoutButton />
      <div className={styles.card}>
        <h1 className={styles.title}>{t("onboarding.connect.title")}</h1>
        <p className={styles.subtitle}>{t("onboarding.connect.subtitle")}</p>
        {(errCode || !canConnect) && (
          <p className={styles.error}>
            {tOr(`onboarding.connect.error.${errCode ?? "forbidden"}`, "onboarding.connect.error.unknown")}
          </p>
        )}
        <button type="button" className={styles.submit} disabled={busy || !canConnect} onClick={() => void onConnect()}>
          {busy ? t("common.loading") : t("onboarding.connect.submit")}
        </button>
        <p className={styles.oauthHint}>{t("onboarding.connect.hint")}</p>
      </div>
    </main>
  );
}

// LogoutButton — top-right escape hatch shown on both onboarding sub-views.
// The user may want to abandon the flow if they signed up with the wrong
// email or want to switch accounts; without this they'd be stuck on
// /onboarding with no way back to /login.
function LogoutButton() {
  const { t } = useLocale();
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();

  async function onClick() {
    const target = await logout();
    navigate(target, { replace: true });
  }

  return (
    <button
      type="button"
      className={styles.signoutBtn}
      onClick={() => void onClick()}
    >
      {t("auth.signout")}
    </button>
  );
}

function SyncProgress() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [status, setStatus] = useState<SyncStatusResponse | null>(null);
  const [retrying, setRetrying] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function poll() {
      try {
        const res = await api.get<SyncStatusResponse>(
          "/api/sync/status",
        );
        if (cancelledRef.current) return;
        setStatus(res);
        if (res.status === "completed") {
          navigate("/chat", { replace: true });
          return;
        }
      } catch (err) {
        // Network blip — continue polling.
        console.warn("sync status poll failed", err);
      }
      if (!cancelledRef.current) {
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    }

    void poll();
    return () => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [navigate]);

  async function onRetry() {
    setRetrying(true);
    try {
      await api.post("/api/sync/retry");
      setStatus(null);
    } finally {
      setRetrying(false);
    }
  }

  return (
    <main className={styles.shell}>
      <LogoutButton />
      <div className={styles.card}>
        <h1 className={styles.title}>{t("onboarding.title")}</h1>
        <p className={styles.subtitle}>{t("onboarding.subtitle")}</p>

        {(!status || status.status === "pending") && (
          <p className={styles.status}>{t("onboarding.status.pending")}</p>
        )}

        {status?.status === "running" && (
          <>
            <p className={styles.status}>
              {t("onboarding.status.running", {
                records: status.records_fetched ?? 0,
              })}
            </p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
            <ul className={styles.entityList}>
              {SYNC_ENTITIES.map((e) => {
                const p = status.progress?.[e.key];
                return (
                  <li key={e.key} className={`${styles.entityItem} ${entityStateClass(p)}`}>
                    <span className={styles.entityIcon} aria-hidden="true" />
                    <span className={styles.entityLabel}>{t(e.labelKey)}</span>
                    {p?.status === "done" && (
                      <span className={styles.entityCount}>{p.count}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {status?.status === "failed" && (
          <>
            <p className={styles.error}>
              {t("onboarding.status.failed", {
                message: status.error_message ?? "",
              })}
            </p>
            <button
              type="button"
              className={styles.retryBtn}
              onClick={() => void onRetry()}
              disabled={retrying}
            >
              {retrying ? t("common.loading") : t("onboarding.retry")}
            </button>
          </>
        )}
      </div>
    </main>
  );
}
